import { Answer } from "./answer";
import { CalendarDate } from "./calendar-date";
import { Community } from "./community";
import { Contact } from "./contact";
import { Insurance } from "./insurance";
import { Invoice } from "./invoice";
import { Note } from "./note";
import { Task } from "./task";
import { toPhoneNumberE164 } from "./telecom";
import { Maybe } from "./util/maybe";
export class ClientIntakeDetailInfo {
    constructor() {
        this.name = "";
        this.preferredName = "";
        this.email = "";
        this.phone = toPhoneNumberE164("");
        this.cellPhone = toPhoneNumberE164("");
        this.birthday = null;
        this.isBirthdayFake = null; // need to initialize as "null" so that an unset value can be detected in the AgeBirthdayPicker.
        this.gender = "";
        this.weight = "";
        this.height = "";
        this.maritalStatus = "";
        this.budgetMonthly = "";
        this.budgetTotal = "";
        this.otherHealthNotes = "";
        this.answers = [];
    }
}
const ClientDetailInfoCloneHelper = (source, theClone) => {
    if (!source) {
        return;
    }
    theClone.name = source.name;
    theClone.preferredName = source.preferredName;
    theClone.email = source.email;
    theClone.phone = toPhoneNumberE164(source.phone);
    theClone.cellPhone = toPhoneNumberE164(source.cellPhone);
    theClone.birthday = source.birthday;
    theClone.isBirthdayFake = source.isBirthdayFake;
    theClone.gender = source.gender;
    theClone.weight = source.weight;
    theClone.height = source.height;
    theClone.maritalStatus = source.maritalStatus;
    theClone.insuranceInfo = Insurance.clone((source.insuranceInfo || {}));
    theClone.budgetMonthly = source.budgetMonthly;
    theClone.budgetTotal = source.budgetTotal;
    theClone.otherHealthNotes = source.otherHealthNotes;
    theClone.answers = (source.answersField || source.answers || []).map(a => Answer.load(a));
};
export const powerOfAttorneyDescription = {
    empty: "None",
    all: "Full",
    financial: "Fiduciary",
    financial_real: "Financial",
    medic: "Medical",
    guardian: "Guardian",
};
export const formatPowerOfAttorney = (powerOfAttorney) => {
    if (!powerOfAttorney)
        return "";
    const allPoas = (typeof powerOfAttorney === "string" ? [powerOfAttorney] : powerOfAttorney);
    return allPoas
        .map(poaKey => powerOfAttorneyDescription[poaKey] || poaKey)
        .join(", ");
};
export const clientLocationTypes = {
    AdultFamilyHome: "Adult Family Home",
    AssistedLiving: "Assisted Living",
    "Home_Family ": "Home (with family)", // DO NOT DELETE THE EXTRA SPACE IN THE KEY ON THIS LINE. It was a mistake, but now we have used it as a key.
    Home: "Home (own)",
    Hospital: "Hospital",
    IndependentLiving: "Independent Living",
    "Long-Term Care": "Long-Term Care",
    MemoryCare: "Memory Care",
    Rehab: "Rehab",
    "Skilled Nursing": "Skilled Nursing",
    Other: "Other",
};
export const ClientMaritalStatuses = [
    { value: "Single", text: "Single" },
    { value: "Married", text: "Married" },
    { value: "Widow", text: "Widow(er)" },
    {
        value: "Domestic-Partner",
        text: "Domestic Partner",
    },
    {
        value: "Non-married Couple",
        text: "Non-married Couple",
    },
    { value: "Divorced", text: "Divorced" },
    { value: "Separated", text: "Legally Separated" },
];
export const ClientGenderOptions = [
    { value: "Female", text: "Female" },
    { value: "Male", text: "Male" },
    { value: "Other", text: "Other" },
];
// define this string in a central place so it's easy to update everywhere all at once if needed
export const SAME_FOR_BOTH_STR = "Same for both";
function compareClientCommunities(a, b) {
    if (typeof a.order === "number" && typeof b.order === "number") {
        return a.order - b.order;
    }
    if (typeof a.order === "number" && typeof b.order !== "number") {
        return 1;
    }
    if (typeof a.order !== "number" && typeof b.order === "number") {
        return -1;
    }
    const aName = Maybe.fromValue(a.community.name)
        .map(n => n.toLocaleLowerCase())
        .getOrElse("");
    const bName = Maybe.fromValue(b.community.name)
        .map(n => n.toLocaleLowerCase())
        .getOrElse("");
    return aName.localeCompare(bName);
}
// NOTE: AdditionalClient needs to be defined before Client because client references it.
export class AdditionalClient extends ClientIntakeDetailInfo {
    constructor() {
        //implements IAdditionalClient {
        // name: string = "";
        // email: string = "";
        // phone: PhoneNumberE164 = toPhoneNumberE164("");
        // cellPhone: PhoneNumberE164 = toPhoneNumberE164("");
        // birthday: CalendarDate | null = null;
        // isBirthdayFake: boolean | null = null; // need to initialize as "null" so that an unset value can be detected in the AgeBirthdayPicker.
        // gender: string = "";
        // weight: string = "";
        // height: string = "";
        // maritalStatus: string = "";
        // insuranceInfo: IInsurance = new Insurance();
        // budgetMonthly: string = "";
        // budgetTotal: string = "";
        // otherHealthNotes: string = "";
        // physicianContacts: IContact[] = [];
        // answers: IAnswer[] = [];
        super(...arguments);
        this.id = null;
        this.relationshipToClient = "";
        this.physicianContacts = [];
        this.physicianContactsSameAsPrimaryClient = false;
        this.activityAnswersSameAsPrimaryClient = false;
        this.servicesAnswersSameAsPrimaryClient = false;
        this.typeAnswersSameAsPrimaryClient = true;
        this.financeAnswersSameAsPrimaryClient = true;
        this.amenityAnswersSameAsPrimaryClient = false;
        this.staffAnswersSameAsPrimaryClient = false;
        this.otherDetailsAnswersSameAsPrimaryClient = false;
        this.summaryAnswersSameAsPrimaryClient = true;
        this.socialInterestsAnswersSameAsPrimaryClient = false;
        this.communityRecordsAnswersSameAsPrimaryClient = false;
        this.contractAnswersSameAsPrimaryClient = false;
        this.insuranceAnswersSameAsPrimaryClient = false;
        this.physicianAnswersSameAsPrimaryClient = false;
    }
    clone() {
        return AdditionalClient.clone(this);
    }
    static clone(source) {
        var theClone = new AdditionalClient();
        if (!source) {
            return theClone;
        }
        ClientDetailInfoCloneHelper(source, theClone);
        theClone.id = source.id;
        theClone.relationshipToClient = source.relationshipToClient;
        theClone.physicianContactsSameAsPrimaryClient =
            source.physicianContactsSameAsPrimaryClient;
        theClone.physicianContacts = (source.physicianContacts || []).map(c => {
            const contact = Contact.clone(c);
            contact.referenceId = c.referenceId;
            return contact;
        });
        theClone.activityAnswersSameAsPrimaryClient =
            source.activityAnswersSameAsPrimaryClient;
        theClone.servicesAnswersSameAsPrimaryClient =
            source.servicesAnswersSameAsPrimaryClient;
        theClone.typeAnswersSameAsPrimaryClient = source.typeAnswersSameAsPrimaryClient;
        theClone.financeAnswersSameAsPrimaryClient =
            source.financeAnswersSameAsPrimaryClient;
        theClone.amenityAnswersSameAsPrimaryClient =
            source.amenityAnswersSameAsPrimaryClient;
        theClone.staffAnswersSameAsPrimaryClient = source.staffAnswersSameAsPrimaryClient;
        theClone.otherDetailsAnswersSameAsPrimaryClient =
            source.otherDetailsAnswersSameAsPrimaryClient;
        theClone.summaryAnswersSameAsPrimaryClient =
            source.summaryAnswersSameAsPrimaryClient;
        theClone.socialInterestsAnswersSameAsPrimaryClient =
            source.socialInterestsAnswersSameAsPrimaryClient;
        theClone.communityRecordsAnswersSameAsPrimaryClient =
            source.communityRecordsAnswersSameAsPrimaryClient;
        theClone.contractAnswersSameAsPrimaryClient =
            source.contractAnswersSameAsPrimaryClient;
        theClone.insuranceAnswersSameAsPrimaryClient =
            source.insuranceAnswersSameAsPrimaryClient;
        theClone.physicianAnswersSameAsPrimaryClient =
            source.physicianAnswersSameAsPrimaryClient;
        return theClone;
    }
    static load(sourceDoc) {
        const result = AdditionalClient.clone(sourceDoc);
        return result;
    }
}
export const MAX_ALLOWED_BULK_CLIENT_COMMUNITIES = 50;
export var CommunityRelationshipType;
(function (CommunityRelationshipType) {
    CommunityRelationshipType[CommunityRelationshipType["matched"] = 1] = "matched";
    CommunityRelationshipType[CommunityRelationshipType["excluded"] = 2] = "excluded";
    CommunityRelationshipType[CommunityRelationshipType["featured"] = 3] = "featured";
})(CommunityRelationshipType || (CommunityRelationshipType = {}));
const communityRelationshipLabel = (type) => {
    switch (type) {
        case CommunityRelationshipType.matched:
            return "Potential Match (Not in Family View)";
        case CommunityRelationshipType.excluded:
            return "Excluded";
        case CommunityRelationshipType.featured:
            return "Potential Match (In Family View)";
        default:
            const exhaustiveCheck = type;
            throw new Error(`Unhandled CommunityRelationshipType: ${exhaustiveCheck}`);
    }
};
export const communityRelationshipChoices = () => {
    return Object.keys(CommunityRelationshipType)
        .map(t => {
        const typeAsNumber = parseInt(t, 10);
        if (typeAsNumber > 0) {
            return {
                value: t,
                label: communityRelationshipLabel(typeAsNumber),
            };
        }
        return null;
    })
        .filter(t => !!t);
};
export class Client extends ClientIntakeDetailInfo {
    constructor() {
        super(...arguments);
        this.name = "";
        this.preferredName = "";
        this.email = "";
        this.phone = toPhoneNumberE164("");
        this.cellPhone = toPhoneNumberE164("");
        this.birthday = null;
        this.isBirthdayFake = null; // need to initialize as "null" so that an unset value can be detected in the AgeBirthdayPicker.
        this.gender = "";
        this.weight = "";
        this.height = "";
        this.maritalStatus = "";
        this.budgetMonthly = "";
        this.budgetTotal = "";
        this.otherHealthNotes = "";
        this.physicianContacts = [];
        this.answers = [];
        this.contactEntities = [];
        this.address = "";
        this.city = "";
        this.state = "";
        this.zip = "";
        this.emailOptOut = false;
        this.tenantId = null;
        this.statusListItemId = null;
        this.statusClosedDate = null;
        this.statusCheckLaterDate = null;
        this.properties = {};
        this.newTask = new Task();
        this.notes = [];
        this.draftNote = "";
        this.bestContactEntity = new Contact();
        this.powerOfAttorney = new Contact();
        this.powerOfAttorneyEntity = new Contact();
        this.hasPowerOfAttorney = false;
        this.powerOfAttorneySameAsBestContact = false;
        this.additionalClient = null;
        this.moveTimeline = null;
        this.locationName = "";
        this.currentLocationType = "";
        this.dischargeDate = null;
        this.communities = [];
        this.excludeCommunities = [];
        this.clientCommunities = [];
        this.noCompareList = [];
        this.assignedUserId = null;
        this.referralSourceId = null;
        this.referralBusinessId = null;
        this.referralContactId = null;
        this.useBestContactAddressForBestLocation = false;
        this.bestLocationAddress = "";
        this.bestLocationCity = "";
        this.bestLocationState = "";
        this.bestLocationZip = "";
        this.clientSameAsBestContact = false;
        this.baseLocation = null;
        this.contactedOtherAgency = false;
        this.touredPreviously = false;
        this.touredPreviouslyNotes = "";
        this.contactedOtherAgencyNotes = "";
        this.signedAgencyAgreement = false;
        this.spouseNeedsCare = false;
        this.agencyAgreementDate = null;
        this.otherLocationNotes = "";
        this.currentLocationNotes = "";
        this.agreementNotes = "";
        this.moveTimelineActual = null;
        this.invoices = [];
        this.invoiceSentDate = null;
        this.invoiceAmount = "";
        this.paymentReceivedDate = null;
        this.chosenCommunityId = null;
        this.updatedAt = new Date();
        this.createdBy = null;
        this.updatedBy = null;
        this.referralNotes = "";
        this._dateAdded = new Date();
        this.dateAddedManualOverride = null;
        this.region = "";
        this.regionId = null;
        this.id = null;
        this.sourceId = null;
        this.archived = false;
        this.version = 0;
        this.serial = 0;
    }
    get physicianContactEntities() {
        if (this.contactEntities) {
            return this.contactEntities.filter(c => c.type === "physician");
        }
        return [];
    }
    geocodeableRepresentation() {
        if (typeof this.bestLocationZip == "undefined" || !this.bestLocationZip) {
            if (typeof this.bestLocationCity == "undefined" || !this.bestLocationCity) {
                return null;
            }
            if (typeof this.bestLocationState == "undefined" || !this.bestLocationState) {
                return null;
            }
        }
        return `${this.bestLocationAddress}, ${this.bestLocationCity}, ${this.bestLocationState}, ${this.bestLocationZip}`;
    }
    clone() {
        return Client.clone(this);
    }
    static clone(source) {
        var theClone = new Client();
        ClientDetailInfoCloneHelper(source, theClone);
        theClone.id = source.id;
        theClone.address = source.address;
        theClone.bestContactEntity = Contact.load(source.bestContactEntity || {});
        theClone.contactEntities = (source.contactEntities || []).map(n => Contact.load(n));
        theClone.powerOfAttorney = Contact.load(source.powerOfAttorney || {});
        theClone.powerOfAttorneyEntity = Contact.load(source.powerOfAttorneyEntity || {});
        theClone.hasPowerOfAttorney = source.hasPowerOfAttorney;
        theClone.emailOptOut = source.emailOptOut;
        theClone.powerOfAttorneySameAsBestContact =
            source.powerOfAttorneySameAsBestContact;
        theClone.additionalClient = source.additionalClient
            ? AdditionalClient.clone(source.additionalClient)
            : null;
        theClone.city = source.city;
        theClone.communities = source.communities || [];
        theClone.excludeCommunities = source.excludeCommunities || [];
        theClone.clientCommunities = (source.clientCommunities || []).map(cc => {
            return { ...cc, community: cc.community.clone(), client: theClone };
        });
        theClone.noCompareList = source.noCompareList || [];
        theClone.currentLocationType = source.currentLocationType;
        theClone.dischargeDate = source.dischargeDate
            ? CalendarDate.parse(source.dischargeDate).getOrElse(null)
            : null;
        theClone.locationName = source.locationName;
        theClone.moveTimeline = source.moveTimeline
            ? CalendarDate.parse(source.moveTimeline).getOrElse(null)
            : null;
        theClone.draftNote = source.draftNote;
        theClone.newTask = Task.load(source.newTask || {});
        theClone.notes = (source.notes || []).map(n => Note.load(n));
        theClone.properties = { ...source.properties };
        theClone.state = source.state;
        theClone.statusListItemId = source.statusListItemId;
        theClone.statusClosedDate = source.statusClosedDate
            ? CalendarDate.parse(source.statusClosedDate).getOrElse(null)
            : null;
        theClone.statusCheckLaterDate = source.statusCheckLaterDate
            ? CalendarDate.parse(source.statusCheckLaterDate).getOrElse(null)
            : null;
        theClone.tenantId = source.tenantId;
        theClone.assignedUserId = source.assignedUserId;
        theClone.referralSourceId = source.referralSourceId;
        theClone.referralBusinessId = source.referralBusinessId;
        theClone.referralContactId = source.referralContactId;
        theClone.zip = source.zip;
        theClone.useBestContactAddressForBestLocation =
            source.useBestContactAddressForBestLocation;
        theClone.bestLocationAddress = source.bestLocationAddress;
        theClone.bestLocationCity = source.bestLocationCity;
        theClone.bestLocationState = source.bestLocationState;
        theClone.bestLocationZip = source.bestLocationZip;
        theClone.clientSameAsBestContact = source.clientSameAsBestContact;
        theClone.baseLocation = { ...source.baseLocation };
        theClone.otherLocationNotes = source.otherLocationNotes;
        theClone.currentLocationNotes = source.currentLocationNotes;
        theClone.agreementNotes = source.agreementNotes;
        theClone.contactedOtherAgency = source.contactedOtherAgency;
        theClone.touredPreviously = source.touredPreviously;
        theClone.touredPreviouslyNotes = source.touredPreviouslyNotes;
        theClone.contactedOtherAgencyNotes = source.contactedOtherAgencyNotes;
        theClone.signedAgencyAgreement = source.signedAgencyAgreement;
        theClone.spouseNeedsCare = source.spouseNeedsCare;
        theClone.agencyAgreementDate = source.agencyAgreementDate
            ? CalendarDate.parse(source.agencyAgreementDate).getOrElse(null)
            : null;
        theClone.chosenCommunityId = source.chosenCommunityId;
        theClone.moveTimelineActual = source.moveTimelineActual
            ? CalendarDate.parse(source.moveTimelineActual).getOrElse(null)
            : null;
        theClone.invoices = source.invoices
            ? source.invoices.map(i => Invoice.clone(i))
            : [];
        theClone.invoiceSentDate = source.invoiceSentDate
            ? CalendarDate.parse(source.invoiceSentDate).getOrElse(null)
            : null;
        theClone.invoiceAmount = source.invoiceAmount;
        theClone.paymentReceivedDate = source.paymentReceivedDate
            ? CalendarDate.parse(source.paymentReceivedDate).getOrElse(null)
            : null;
        theClone._dateAdded = source._dateAdded
            ? new Date(source._dateAdded)
            : new Date();
        theClone.region = source.region;
        theClone.dateAddedManualOverride = source.dateAddedManualOverride
            ? CalendarDate.parse(source.dateAddedManualOverride).getOrElse(null)
            : null;
        theClone.updatedAt = source.updatedAt ? new Date(source.updatedAt) : new Date();
        theClone.updatedBy = source.updatedBy;
        theClone.createdBy = source.createdBy;
        theClone.referralNotes = source.referralNotes;
        theClone.archived = Maybe.fromValue(source)
            .map(s => s.archived)
            .getOrElse(false);
        theClone.sourceId = Maybe.fromValue(source)
            .map(s => s.sourceId)
            .getOrElse(null);
        theClone.version = source.version || 0;
        theClone.serial = source.serial || 0;
        theClone.regionId = source.regionId;
        return theClone;
    }
    static load(source) {
        const theClone = new Client();
        ClientDetailInfoCloneHelper(source, theClone);
        theClone.id = source.id;
        theClone.address = source.address;
        theClone.bestContactEntity = Contact.load(source.bestContactEntity || {});
        theClone.contactEntities = (source.contactEntities || []).map((n) => Contact.load(n));
        theClone.powerOfAttorney = Contact.load(source.powerOfAttorney || {});
        theClone.powerOfAttorneyEntity = Contact.load(source.powerOfAttorneyEntity || {});
        theClone.hasPowerOfAttorney = source.hasPowerOfAttorney;
        theClone.emailOptOut = source.emailOptOut;
        theClone.powerOfAttorneySameAsBestContact =
            source.powerOfAttorneySameAsBestContact;
        theClone.additionalClient = source.additionalClient
            ? AdditionalClient.clone(source.additionalClient)
            : null;
        theClone.city = source.city;
        theClone.communities = (source.communities || []).map((x) => x);
        theClone.excludeCommunities = (source.excludeCommunities || []).map((x) => x);
        theClone.clientCommunities = (source.clientCommunities || []).map((cc) => {
            return {
                ...cc,
                community: Community.load(cc.community).clone(),
                client: theClone,
            };
        });
        theClone.noCompareList = (source.noCompareList || []).map((x) => x);
        theClone.currentLocationType = source.currentLocationType;
        theClone.dischargeDate = source.dischargeDate
            ? CalendarDate.parse(source.dischargeDate).getOrElse(null)
            : null;
        theClone.locationName = source.locationName;
        theClone.moveTimeline = source.moveTimeline
            ? CalendarDate.parse(source.moveTimeline).getOrElse(null)
            : null;
        theClone.draftNote = source.draftNote;
        theClone.newTask = Task.load(source.newTask || {});
        theClone.notes = (source.notes || []).map((n) => Note.load(n));
        theClone.properties = { ...source.properties };
        theClone.state = source.state;
        theClone.statusListItemId = source.statusListItemId;
        theClone.statusClosedDate = source.statusClosedDate
            ? CalendarDate.parse(source.statusClosedDate).getOrElse(null)
            : null;
        theClone.statusCheckLaterDate = source.statusCheckLaterDate
            ? CalendarDate.parse(source.statusCheckLaterDate).getOrElse(null)
            : null;
        theClone.tenantId = source.tenantId;
        theClone.assignedUserId = source.assignedUserId;
        theClone.referralSourceId = source.referralSourceId;
        theClone.referralBusinessId = source.referralBusinessId;
        theClone.referralContactId = source.referralContactId;
        theClone.zip = source.zip;
        theClone.useBestContactAddressForBestLocation =
            source.useBestContactAddressForBestLocation;
        theClone.bestLocationAddress = source.bestLocationAddress;
        theClone.bestLocationCity = source.bestLocationCity;
        theClone.bestLocationState = source.bestLocationState;
        theClone.bestLocationZip = source.bestLocationZip;
        theClone.clientSameAsBestContact = source.clientSameAsBestContact;
        theClone.baseLocation = { ...source.baseLocation };
        theClone.otherLocationNotes = source.otherLocationNotes;
        theClone.currentLocationNotes = source.currentLocationNotes;
        theClone.agreementNotes = source.agreementNotes;
        theClone.contactedOtherAgency = source.contactedOtherAgency;
        theClone.touredPreviously = source.touredPreviously;
        theClone.touredPreviouslyNotes = source.touredPreviouslyNotes;
        theClone.contactedOtherAgencyNotes = source.contactedOtherAgencyNotes;
        theClone.signedAgencyAgreement = source.signedAgencyAgreement;
        theClone.spouseNeedsCare = source.spouseNeedsCare;
        theClone.agencyAgreementDate = source.agencyAgreementDate
            ? CalendarDate.parse(source.agencyAgreementDate).getOrElse(null)
            : null;
        theClone.chosenCommunityId = source.chosenCommunityId;
        theClone.moveTimelineActual = source.moveTimelineActual
            ? CalendarDate.parse(source.moveTimelineActual).getOrElse(null)
            : null;
        theClone.invoices = source.invoices
            ? source.invoices.map((i) => Invoice.Load(i))
            : [];
        theClone.invoiceSentDate = source.invoiceSentDate
            ? CalendarDate.parse(source.invoiceSentDate).getOrElse(null)
            : null;
        theClone.invoiceAmount = source.invoiceAmount;
        theClone.paymentReceivedDate = source.paymentReceivedDate
            ? CalendarDate.parse(source.paymentReceivedDate).getOrElse(null)
            : null;
        theClone._dateAdded = source.dateAdded ? new Date(source.dateAdded) : new Date();
        theClone.region = source.region;
        theClone.dateAddedManualOverride = source.dateAddedManualOverride
            ? CalendarDate.parse(source.dateAddedManualOverride).getOrElse(null)
            : null;
        theClone.updatedAt = source.updatedAt ? new Date(source.updatedAt) : new Date();
        theClone.updatedBy = source.updatedBy;
        theClone.createdBy = source.createdBy;
        theClone.referralNotes = source.referralNotes;
        theClone.archived = Maybe.fromValue(source)
            .map(s => s.archived)
            .getOrElse(false);
        theClone.sourceId = Maybe.fromValue(source)
            .map(s => s.sourceId)
            .getOrElse(null);
        theClone.version = source.version || 0;
        theClone.serial = source.serial || 0;
        theClone.regionId = source.regionId;
        return theClone;
    }
    formatClientAndAdditionalClientNames(connector = " and ") {
        const hasName1 = this.name && this.name !== "";
        const hasName2 = this.additionalClient?.name && this.additionalClient.name !== "";
        if (hasName1 && hasName2) {
            return `${this.name}${connector}${this.additionalClient?.name}`;
        }
        if (hasName1) {
            return this.name;
        }
        if (hasName2) {
            return this.additionalClient?.name;
        }
        return "(no names)";
    }
    formatName(name1, name2) {
        const hasName1 = name1 && name1 !== "";
        const hasName2 = name2 && name2 !== "";
        if (hasName1 && hasName2) {
            return `${name1} (${name2})`;
        }
        if (hasName1) {
            return name1;
        }
        if (hasName2) {
            return name2;
        }
        return "(no name)";
    }
    get preferredClientName() {
        return this.formatName(this.name, undefined);
    }
    get dateAdded() {
        return this.dateAddedManualOverride
            ? this.dateAddedManualOverride.toDate()
            : this._dateAdded;
    }
    get matchedCommunities() {
        if (!this.clientCommunities) {
            return [];
        }
        return this.clientCommunities
            .filter(cc => cc.relationship == CommunityRelationshipType.matched)
            .map(cc => cc.community);
    }
    get matchedCommunityIds() {
        return this.matchedCommunities.filter(c => !!c).map(c => c.id);
    }
    get allCommunities() {
        if (!this.clientCommunities) {
            return [];
        }
        return this.clientCommunities
            .filter(cc => cc.relationship !== CommunityRelationshipType.excluded)
            .sort((a, b) => compareClientCommunities(a, b))
            .map(cc => cc.community);
    }
    get allCommunityIds() {
        return this.allCommunities.filter(c => !!c).map(c => c.id);
    }
    get excludedCommunities() {
        if (!this.clientCommunities) {
            return [];
        }
        return this.clientCommunities
            .filter(cc => cc.relationship == CommunityRelationshipType.excluded)
            .map(cc => cc.community);
    }
    get excludedCommunityIds() {
        return this.excludedCommunities.filter(c => !!c).map(c => c.id);
    }
    get referralSourceMap() {
        return Maybe.fromValue(this.referralSourceId)
            .map(x => x.split(";", 3))
            .map(x => {
            return {
                identifier: Maybe.fromValue(x[0]).map(s => (s.length > 0 ? s : null)),
                business: Maybe.fromValue(x[1]).map(s => (s.length > 0 ? s : null)),
                contact: Maybe.fromValue(x[2]).map(s => (s.length > 0 ? s : null)),
            };
        })
            .getOrElse({
            identifier: Maybe.none(),
            contact: Maybe.none(),
            business: Maybe.none(),
        }); // TODO remove type assertion
    }
    get singleLineAddress() {
        if (this.address && (this.city || this.state || this.zip)) {
            return this.address + ", " + this.formattedCityStateZip;
        }
        if (this.address) {
            return this.address;
        }
        return this.formattedCityStateZip;
    }
    get formattedCityStateZip() {
        var retVal = null;
        var hasCity = this.city && this.city != "";
        var hasState = this.state && this.state != "";
        if (hasCity && hasState) {
            retVal = this.city + ", " + this.state;
        }
        else if (hasCity) {
            retVal = this.city;
        }
        else {
            retVal = this.state;
        }
        if (this.zip) {
            if (retVal) {
                if (hasCity && !hasState) {
                    retVal += ",";
                }
                retVal += " ";
            }
            else {
                retVal = "";
            }
            retVal += this.zip;
        }
        return retVal;
    }
    static newForRegion(region) {
        const c = new Client();
        c.region = region;
        return c;
    }
}
//# sourceMappingURL=client.js.map